function getStorylinePoint(point) {
  if (Array.isArray(point)) {
    return { x: Number(point[0]), y: Number(point[1]) }
  }
  return { x: Number(point.x), y: Number(point.y) }
}

function getBandHorizontalRange(band, evidenceVisuals = []) {
  const xs = []
  if (band && band.visualBounds) {
    xs.push(band.visualBounds.x)
    xs.push(band.visualBounds.x + Math.max(band.visualBounds.width, 280))
  }
  evidenceVisuals.forEach(point => {
    const radius = point.radius || 24
    xs.push(point.x - radius, point.x + radius)
  })
  if (!xs.length) return null
  return {
    minX: Math.min(...xs) - 80,
    maxX: Math.max(...xs) + 80,
  }
}

function collectSegmentYsInRange(first, second, minX, maxX, ys) {
  if (
    !Number.isFinite(first.x) ||
    !Number.isFinite(first.y) ||
    !Number.isFinite(second.x) ||
    !Number.isFinite(second.y)
  ) {
    return
  }
  const segmentMinX = Math.min(first.x, second.x)
  const segmentMaxX = Math.max(first.x, second.x)
  if (segmentMaxX < minX || segmentMinX > maxX) return

  if (first.x >= minX && first.x <= maxX) ys.push(first.y)
  if (second.x >= minX && second.x <= maxX) ys.push(second.y)

  if (first.x === second.x) {
    if (first.x >= minX && first.x <= maxX) {
      ys.push(first.y, second.y)
    }
    return
  }

  const overlapStart = Math.max(minX, segmentMinX)
  const overlapEnd = Math.min(maxX, segmentMaxX)
  const samples = 8
  for (let index = 0; index <= samples; index += 1) {
    const x = overlapStart + ((overlapEnd - overlapStart) * index) / samples
    const ratio = (x - first.x) / (second.x - first.x)
    if (ratio >= 0 && ratio <= 1) {
      ys.push(first.y + (second.y - first.y) * ratio)
    }
  }
}

function getStorylineTopYInRange({ currentStorylines }, minX, maxX) {
  const ys = []
  currentStorylines.forEach(storyline => {
    if (!Array.isArray(storyline) || !storyline.length) return
    const points = storyline.map(getStorylinePoint)
    points.forEach(point => {
      if (
        Number.isFinite(point.x) &&
        Number.isFinite(point.y) &&
        point.x >= minX &&
        point.x <= maxX
      ) {
        ys.push(point.y)
      }
    })
    for (let index = 0; index < points.length - 1; index += 1) {
      collectSegmentYsInRange(points[index], points[index + 1], minX, maxX, ys)
    }
  })
  return ys.length ? Math.min(...ys) : null
}

function isAuthoringOrEventNode(node) {
  return Boolean(
    node &&
      node.closest &&
      node.closest(
        [
          '#authoring-highlight-bands',
          '#authoring-band-tooltip',
          '#authoring-band-emphasis',
          '#authoring-selection-marks',
          '.event-icon-group',
          '.mock-event-dotted-line',
        ].join(',')
      )
  )
}

function getRenderedStorylineTopYInRange({ mySvg }, minX, maxX) {
  if (!mySvg || !mySvg.querySelectorAll) return null
  const ys = []
  const geometryNodes = Array.from(mySvg.querySelectorAll('path,line,polyline'))
  geometryNodes.forEach(node => {
    if (isAuthoringOrEventNode(node)) return
    const stroke = node.getAttribute('stroke')
    if (!stroke || stroke === 'none') return
    if (node.getTotalLength && node.getPointAtLength) {
      const length = node.getTotalLength()
      if (!Number.isFinite(length) || length < 80) return
      const samples = Math.max(16, Math.ceil(length / 80))
      for (let index = 0; index <= samples; index += 1) {
        const point = node.getPointAtLength((length * index) / samples)
        if (point.x >= minX && point.x <= maxX && Number.isFinite(point.y)) {
          ys.push(point.y)
        }
      }
      return
    }

    const bbox = node.getBBox && node.getBBox()
    if (!bbox || bbox.width < 80) return
    if (bbox.x + bbox.width >= minX && bbox.x <= maxX) {
      ys.push(bbox.y)
    }
  })
  return ys.length ? Math.min(...ys) : null
}

function getBandTrajectoryTopY(context, band) {
  const { currentGraph, eventById, playerColour } = context
  if (!band || !currentGraph) return null
  const startTimestamp = Number(band.startTimestamp)
  const endTimestamp = Number(band.endTimestamp)
  const evidenceTimestamps = (band.evidenceEventIds || [])
    .map(eventId => eventById.get(eventId))
    .filter(Boolean)
    .map(event => Number(event.timestamp))
    .filter(Number.isFinite)
  const timestamps = []
  if (Number.isFinite(startTimestamp) && Number.isFinite(endTimestamp)) {
    const start = Math.min(startTimestamp, endTimestamp)
    const end = Math.max(startTimestamp, endTimestamp)
    const steps = start === end ? 1 : 18
    for (let index = 0; index <= steps; index += 1) {
      timestamps.push(start + ((end - start) * index) / steps)
    }
  }
  evidenceTimestamps.forEach(timestamp => timestamps.push(timestamp))
  const uniqueTimestamps = Array.from(new Set(timestamps))
  if (!uniqueTimestamps.length) return null

  const xMin = band.visualBounds ? band.visualBounds.x - 120 : -Infinity
  const xMax = band.visualBounds
    ? band.visualBounds.x + band.visualBounds.width + 120
    : Infinity
  const playerIds = playerColour
    ? Object.keys(playerColour)
    : ['Player1', 'Player2', 'Player3', 'Player4']
  const ys = []
  uniqueTimestamps.forEach(timestamp => {
    playerIds.forEach(playerId => {
      try {
        const x = currentGraph.getCharacterX(playerId, timestamp)
        const y = currentGraph.getCharacterY(playerId, timestamp)
        if (
          Number.isFinite(x) &&
          Number.isFinite(y) &&
          x >= xMin &&
          x <= xMax &&
          y >= 0
        ) {
          ys.push(y)
        }
      } catch (error) {
        // Some sampled timestamps may fall outside a character path.
      }
    })
  })
  return ys.length ? Math.min(...ys) : null
}

function getAuthorBandRibbonY(context, band, evidenceVisuals = []) {
  const { authorBandMinRibbonY, authorBandStorylineGap } = context
  const range = getBandHorizontalRange(band, evidenceVisuals)
  const renderedTopStorylineY = range
    ? getRenderedStorylineTopYInRange(context, range.minX, range.maxX)
    : null
  const graphTopStorylineY = range
    ? getStorylineTopYInRange(context, range.minX, range.maxX)
    : null
  const topStorylineY = Math.min(
    ...[renderedTopStorylineY, graphTopStorylineY].filter(Number.isFinite)
  )
  if (Number.isFinite(topStorylineY)) {
    return Math.max(
      authorBandMinRibbonY,
      topStorylineY - authorBandStorylineGap
    )
  }

  const topTrajectoryY = getBandTrajectoryTopY(context, band)
  if (Number.isFinite(topTrajectoryY)) {
    return Math.max(
      authorBandMinRibbonY,
      topTrajectoryY - authorBandStorylineGap
    )
  }

  const topEvidenceY = evidenceVisuals.length
    ? Math.min(...evidenceVisuals.map(point => point.y - (point.radius || 18)))
    : null
  if (Number.isFinite(topEvidenceY)) {
    return Math.max(authorBandMinRibbonY, topEvidenceY - authorBandStorylineGap)
  }
  return authorBandMinRibbonY
}

export function getRenderedAuthorBandRibbonY(
  context,
  band,
  evidenceVisuals = []
) {
  const { bandRibbonYById } = context
  const renderedY = band && bandRibbonYById.get(band.id)
  return Number.isFinite(renderedY)
    ? renderedY
    : getAuthorBandRibbonY(context, band, evidenceVisuals)
}

function rangesOverlap(leftA, rightA, leftB, rightB) {
  return leftA <= rightB && rightA >= leftB
}

function resolveAuthorBandRibbonY(
  { authorBandLaneSpacing, authorBandMinRibbonY },
  baseY,
  ribbonX,
  ribbonWidth,
  occupiedBands
) {
  let ribbonY = baseY
  const xPadding = 24
  const conflicts = y =>
    occupiedBands.some(
      band =>
        rangesOverlap(
          ribbonX - xPadding,
          ribbonX + ribbonWidth + xPadding,
          band.x,
          band.x + band.width
        ) && Math.abs(band.y - y) < authorBandLaneSpacing
    )

  while (
    conflicts(ribbonY) &&
    ribbonY - authorBandLaneSpacing >= authorBandMinRibbonY
  ) {
    ribbonY -= authorBandLaneSpacing
  }
  while (conflicts(ribbonY)) {
    ribbonY += authorBandLaneSpacing
  }
  return ribbonY
}

function removeAuthorBandTooltip(context) {
  const { svg } = context
  if (!svg) return
  const existing = svg.select('#authoring-band-tooltip')
  if (existing) existing.remove()
  resetAuthorBandEmphasis(context)
}

export function resetAuthorBandEmphasis({ svg, eventElementsById }) {
  if (!svg) return
  const emphasis = svg.select('#authoring-band-emphasis')
  if (emphasis) emphasis.remove()
  eventElementsById.forEach(items => {
    items.forEach(item => {
      const node = item.element && (item.element.node || item.element)
      if (!node || !node.setAttribute) return
      if (item.originalTransform) {
        node.setAttribute('transform', item.originalTransform)
      } else {
        node.removeAttribute('transform')
      }
    })
  })
}

export function showAuthorBandEmphasis(context, band) {
  const {
    svg,
    mySvg,
    eventElementsById,
    getEventVisualPoints,
    getIdentitySvgMatrix,
    createSvgMatrix,
    toSvgMatrix,
  } = context
  if (!svg || !mySvg || !band) return
  resetAuthorBandEmphasis(context)
  const color = band.color || '#0f766e'
  const evidenceVisuals = (band.evidenceEventIds || [])
    .map(eventId => {
      const visuals = getEventVisualPoints(eventId)
      return visuals.length ? { eventId, ...visuals[0] } : null
    })
    .filter(Boolean)
  const ribbonY = getRenderedAuthorBandRibbonY(context, band, evidenceVisuals)
  const guideGroup = svg.group().attr({ id: 'authoring-band-emphasis' })
  evidenceVisuals.forEach(point => {
    const radius = point.radius || 18
    const endY = point.y >= ribbonY ? point.y - radius : point.y + radius
    guideGroup.line(point.x, ribbonY, point.x, endY).attr({
      stroke: '#ffffff',
      'stroke-width': 6,
      'stroke-linecap': 'round',
      opacity: 0.78,
      'pointer-events': 'none',
    })
    guideGroup.line(point.x, ribbonY, point.x, endY).attr({
      stroke: color,
      'stroke-width': 3,
      'stroke-dasharray': '7,6',
      'stroke-linecap': 'round',
      opacity: 0.76,
      'pointer-events': 'none',
    })
  })
  svg.append(guideGroup)
  ;(band.evidenceEventIds || []).forEach(eventId => {
    const items = eventElementsById.get(eventId) || []
    items.forEach(item => {
      const node = item.element && (item.element.node || item.element)
      if (!node || !node.setAttribute || !item.visual) return
      const scale = 1.42
      const scaleMatrix = mySvg
        .createSVGMatrix()
        .translate(item.visual.x, item.visual.y)
        .scale(scale)
        .translate(-item.visual.x, -item.visual.y)
      const originalMatrix = createSvgMatrix(
        item.originalMatrix || getIdentitySvgMatrix()
      )
      const scaledMatrix = scaleMatrix.multiply(originalMatrix)
      node.setAttribute('transform', toSvgMatrix(scaledMatrix))
    })
  })
}

function showAuthorBandTooltip(context, band, clientX, clientY) {
  const { svg, mySvg, viewBox, documentRef, eventById, escapeHtml } = context
  if (!svg || !mySvg || !documentRef) return
  const existing = svg.select('#authoring-band-tooltip')
  if (existing) existing.remove()
  showAuthorBandEmphasis(context, band)
  const screenCtm = mySvg.getScreenCTM && mySvg.getScreenCTM()
  if (!screenCtm) return
  const svgPoint = mySvg.createSVGPoint()
  svgPoint.x = clientX
  svgPoint.y = clientY
  const transformed = svgPoint.matrixTransform(screenCtm.inverse())
  const viewport = viewBox || { x: 0, y: 0, w: Infinity, h: Infinity }
  const tooltipWidth = 520
  const tooltipHeight = 220
  let tipX = transformed.x + 28
  let tipY = transformed.y + 24
  if (tipX + tooltipWidth > viewport.x + viewport.w) {
    tipX = transformed.x - tooltipWidth - 28
  }
  if (tipY + tooltipHeight > viewport.y + viewport.h) {
    tipY = transformed.y - tooltipHeight - 24
  }

  const group = svg.group().attr({ id: 'authoring-band-tooltip' })
  group.rect(tipX, tipY, tooltipWidth, tooltipHeight, 10, 10).attr({
    fill: '#ffffff',
    stroke: band.color || '#0f766e',
    'stroke-width': 2,
    opacity: 0.96,
  })
  group
    .text(tipX + 18, tipY + 32, band.tag || band.title || 'Story snippet')
    .attr({
      fill: band.color || '#0f766e',
      'font-size': 22,
      'font-weight': 700,
    })
  const evidenceLabel = `${
    (band.evidenceEventIds || []).length
  } evidence event${(band.evidenceEventIds || []).length === 1 ? '' : 's'}`
  group.text(tipX + 18, tipY + 56, evidenceLabel).attr({
    fill: '#64748b',
    'font-size': 14,
    'font-weight': 600,
  })

  const foreign = documentRef.createElementNS(
    'http://www.w3.org/2000/svg',
    'foreignObject'
  )
  foreign.setAttribute('x', tipX + 18)
  foreign.setAttribute('y', tipY + 70)
  foreign.setAttribute('width', tooltipWidth - 36)
  foreign.setAttribute('height', tooltipHeight - 86)
  const div = documentRef.createElement('div')
  div.style.fontFamily = 'Lato, sans-serif'
  div.style.fontSize = '15px'
  div.style.lineHeight = '1.42'
  div.style.color = '#1f2937'
  div.style.maxHeight = tooltipHeight - 90 + 'px'
  div.style.overflow = 'hidden'
  const evidenceItems = (band.evidenceEventIds || [])
    .map((eventId, index) => {
      const event = eventById.get(eventId)
      if (!event) return ''
      return `<li><strong>${index + 1}. ${escapeHtml(
        event.eventType
      )}</strong> ${escapeHtml(event.eventDetails)}</li>`
    })
    .filter(Boolean)
    .join('')
  div.innerHTML = `
    <div style="margin-bottom:8px;">${escapeHtml(band.summary || '')}</div>
    <ol style="margin:0;padding-left:20px;">${evidenceItems}</ol>
  `
  foreign.appendChild(div)
  group.node.appendChild(foreign)
  svg.append(group)
}

export function renderHighlightBands(context) {
  const {
    svg,
    bandRibbonYById,
    getVisibleSavedHighlightBands,
    getEventVisualPoints,
  } = context
  if (!svg) return
  let bandGroup = svg.select('#authoring-highlight-bands')
  if (!bandGroup) {
    bandGroup = svg.group().attr({ id: 'authoring-highlight-bands' })
    svg.node.insertBefore(bandGroup.node, svg.node.firstChild)
  }
  while (bandGroup.node.firstChild) {
    bandGroup.node.removeChild(bandGroup.node.firstChild)
  }
  bandRibbonYById.clear()
  const occupiedBands = []
  getVisibleSavedHighlightBands().forEach(band => {
    if (!band.visualBounds) return
    const evidenceVisuals = (band.evidenceEventIds || [])
      .map(eventId => {
        const visuals = getEventVisualPoints(eventId)
        return visuals.length ? { eventId, ...visuals[0] } : null
      })
      .filter(Boolean)
      .sort((a, b) => a.x - b.x || a.y - b.y)
    const color = band.color || '#0f766e'
    const ribbonX = band.visualBounds.x
    const ribbonWidth = band.visualBounds.width
    const ribbonY = resolveAuthorBandRibbonY(
      context,
      getAuthorBandRibbonY(context, band, evidenceVisuals),
      ribbonX,
      ribbonWidth,
      occupiedBands
    )
    bandRibbonYById.set(band.id, ribbonY)
    occupiedBands.push({ x: ribbonX, width: ribbonWidth, y: ribbonY })
    bandGroup.line(ribbonX, ribbonY, ribbonX + ribbonWidth, ribbonY).attr({
      stroke: color,
      'stroke-width': 8,
      'stroke-linecap': 'round',
      opacity: 0.55,
      'pointer-events': 'none',
    })
    const hitTarget = bandGroup
      .rect(ribbonX, ribbonY - 30, ribbonWidth, 44, 8, 8)
      .attr({
        fill: '#ffffff',
        opacity: 0.001,
        stroke: 'none',
        cursor: 'help',
      })
    bandGroup.rect(ribbonX, ribbonY - 30, 240, 28, 7, 7).attr({
      fill: '#f8fafc',
      stroke: color,
      'stroke-width': 2,
      opacity: 0.96,
      'pointer-events': 'none',
    })
    bandGroup.text(ribbonX + 12, ribbonY - 10, band.tag).attr({
      fill: color,
      'font-size': 17,
      'font-weight': 700,
      'pointer-events': 'none',
    })
    bandGroup
      .text(
        ribbonX + 250,
        ribbonY - 10,
        `${evidenceVisuals.length} evidence event${
          evidenceVisuals.length === 1 ? '' : 's'
        }`
      )
      .attr({
        fill: '#475569',
        'font-size': 12,
        'font-weight': 600,
        'pointer-events': 'none',
      })
    evidenceVisuals.forEach(point => {
      bandGroup.line(point.x, ribbonY - 12, point.x, ribbonY + 12).attr({
        stroke: '#ffffff',
        'stroke-width': 3,
        'stroke-linecap': 'round',
        opacity: 0.9,
        'pointer-events': 'none',
      })
      bandGroup.circle(point.x, ribbonY, 7).attr({
        fill: color,
        stroke: '#ffffff',
        'stroke-width': 2,
        opacity: 0.95,
        'pointer-events': 'none',
      })
      bandGroup.circle(point.x, point.y, 12).attr({
        fill: 'none',
        stroke: color,
        'stroke-width': 4,
        opacity: 0.38,
        'pointer-events': 'none',
      })
      bandGroup.circle(point.x, point.y, 5).attr({
        fill: '#ffffff',
        stroke: color,
        'stroke-width': 2,
        opacity: 0.95,
        'pointer-events': 'none',
      })
      bandGroup.circle(point.x, point.y, 2).attr({
        fill: color,
        opacity: 0.95,
        'pointer-events': 'none',
      })
    })
    hitTarget.hover(
      event =>
        showAuthorBandTooltip(context, band, event.clientX, event.clientY),
      () => removeAuthorBandTooltip(context)
    )
  })
  svg.node.insertBefore(bandGroup.node, svg.node.firstChild)
  renderActiveInsightBand(context)
}

export function renderActiveInsightBand(context) {
  const {
    svg,
    aiInsightCandidates,
    activeInsightCandidateId,
    getEventVisualPoints,
  } = context
  if (!svg) return
  const existing = svg.select('#ai-insight-suggestion-band')
  if (existing) existing.remove()
  const candidate = aiInsightCandidates.find(
    item => item.id === activeInsightCandidateId
  )
  if (!candidate || !candidate.visualBounds) return

  const group = svg.group().attr({ id: 'ai-insight-suggestion-band' })
  const evidenceVisuals = (candidate.evidenceEventIds || [])
    .map(eventId => {
      const visuals = getEventVisualPoints(eventId)
      return visuals.length ? { eventId, ...visuals[0] } : null
    })
    .filter(Boolean)
    .sort((a, b) => a.x - b.x || a.y - b.y)
  const color = candidate.color || '#6d28d9'
  const ribbonX = candidate.visualBounds.x
  const ribbonWidth = candidate.visualBounds.width
  const ribbonY = getAuthorBandRibbonY(context, candidate, evidenceVisuals)

  group.line(ribbonX, ribbonY, ribbonX + ribbonWidth, ribbonY).attr({
    stroke: color,
    'stroke-width': 7,
    'stroke-dasharray': '12,8',
    'stroke-linecap': 'round',
    opacity: 0.54,
    'pointer-events': 'none',
  })
  group.rect(ribbonX, ribbonY - 30, 250, 28, 7, 7).attr({
    fill: '#ffffff',
    stroke: color,
    'stroke-width': 2,
    'stroke-dasharray': '7,5',
    opacity: 0.96,
    'pointer-events': 'none',
  })
  group.text(ribbonX + 12, ribbonY - 10, candidate.tag).attr({
    fill: color,
    'font-size': 17,
    'font-weight': 700,
    'pointer-events': 'none',
  })
  evidenceVisuals.forEach(point => {
    group.circle(point.x, ribbonY, 7).attr({
      fill: color,
      stroke: '#ffffff',
      'stroke-width': 2,
      opacity: 0.95,
      'pointer-events': 'none',
    })
  })
  svg.node.insertBefore(group.node, svg.node.firstChild)
}

export function getBandFocusBounds(context, band) {
  const { getEventVisualPoints } = context
  if (!band) return null
  const evidenceVisuals = (band.evidenceEventIds || [])
    .flatMap(eventId => getEventVisualPoints(eventId))
    .filter(Boolean)
  const xs = []
  const ys = []

  if (band.visualBounds) {
    xs.push(band.visualBounds.x, band.visualBounds.x + band.visualBounds.width)
  }

  evidenceVisuals.forEach(point => {
    const radius = point.radius || 24
    xs.push(point.x - radius, point.x + radius)
    ys.push(point.y - radius, point.y + radius)
  })

  const ribbonY = getRenderedAuthorBandRibbonY(context, band, evidenceVisuals)
  ys.push(ribbonY - 70, ribbonY + 70)

  if (!xs.length && band.visualBounds) {
    xs.push(band.visualBounds.x, band.visualBounds.x + band.visualBounds.width)
  }
  if (!ys.length && band.visualBounds) {
    ys.push(band.visualBounds.y, band.visualBounds.y + band.visualBounds.height)
  }
  if (!xs.length || !ys.length) return null

  const paddingX = 260
  const paddingY = 140
  return {
    x: Math.min(...xs) - paddingX,
    y: Math.min(...ys) - paddingY,
    width: Math.max(900, Math.max(...xs) - Math.min(...xs) + paddingX * 2),
    height: Math.max(420, Math.max(...ys) - Math.min(...ys) + paddingY * 2),
  }
}

export function getBandStartFocusBounds(context, band) {
  const { getEventVisualPoints, getEventsByIds } = context
  if (!band) return null
  const evidenceVisuals = (band.evidenceEventIds || [])
    .flatMap(eventId => getEventVisualPoints(eventId))
    .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
  const bandStartX = Number.isFinite(band.visualBounds && band.visualBounds.x)
    ? band.visualBounds.x
    : evidenceVisuals.length
    ? Math.min(...evidenceVisuals.map(point => point.x - (point.radius || 24)))
    : null
  if (!Number.isFinite(bandStartX)) return getBandFocusBounds(context, band)

  const firstEvent = getEventsByIds(band.evidenceEventIds)
    .slice()
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp))[0]
  const firstPoint = firstEvent
    ? getEventVisualPoints(firstEvent._eventId)
        .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
        .sort((a, b) => a.y - b.y || a.x - b.x)[0]
    : null
  const ribbonY = getRenderedAuthorBandRibbonY(context, band, evidenceVisuals)
  const ys = []
  if (Number.isFinite(ribbonY)) ys.push(ribbonY - 76, ribbonY + 76)
  if (firstPoint) {
    const radius = firstPoint.radius || 24
    ys.push(firstPoint.y - radius, firstPoint.y + radius)
  }
  if (!ys.length && band.visualBounds) {
    ys.push(band.visualBounds.y, band.visualBounds.y + band.visualBounds.height)
  }
  if (!ys.length) return getBandFocusBounds(context, band)

  return {
    x: bandStartX - 140,
    y: Math.min(...ys) - 120,
    width: 900,
    height: Math.max(420, Math.max(...ys) - Math.min(...ys) + 240),
  }
}
